import { useEffect, useState } from 'react';
import { batches as Api, type Batch, type BatchInput, type BatchSession, type PubState } from '@tvx/api-client';
import { Modal } from '../components/Modal';
import { ImagePick } from '../components/ImagePick';
import { Field, Row } from '../components/Field';

const blankSession = (): BatchSession => ({ topic: '', starts_at: '', meeting_link: '' });

export function BatchForm({ batch, onCancel, onSaved }: {
  batch: Batch | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  /* Only the fields the API takes: the row it sends back also carries the
     joined cover_path, and posting that back is how a form starts failing
     validation nobody wrote. */
  const [f, setF] = useState<BatchInput>(() => batch ? {
    slug: batch.slug, name: batch.name, subtitle: batch.subtitle, fee: batch.fee,
    seats_total: batch.seats_total, seats_sold: batch.seats_sold, platform: batch.platform,
    starts_on: batch.starts_on, start_time: batch.start_time, pass_mark: batch.pass_mark,
    retakes: batch.retakes, cover_media_id: batch.cover_media_id, state: batch.state,
    sessions: [blankSession()],
  } : {
    slug: '', name: '', subtitle: '', fee: 0, seats_total: 0, seats_sold: 0, platform: '',
    starts_on: '', start_time: '', pass_mark: '', retakes: '', cover_media_id: null,
    state: 'draft', sessions: [blankSession()],
  });
  const [cover, setCover] = useState<string | null>(batch?.cover_path ?? null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  /* The list carries a session count, not the timetable. Saving before the
     real sessions arrive would replace the timetable with one blank row. */
  const [loadingSessions, setLoadingSessions] = useState(!!batch);

  useEffect(() => {
    if (!batch) return;
    let alive = true;
    Api.get(batch.id)
      .then(({ item }) => {
        if (!alive) return;
        setF((p) => ({ ...p, sessions: item.sessions?.length ? item.sessions : [blankSession()] }));
      })
      .catch(() => setError('Could not load this batch’s timetable — saving would clear it, so it is blocked.'))
      .finally(() => alive && setLoadingSessions(false));
    return () => { alive = false; };
  }, [batch?.id]);          // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof BatchInput>(k: K, v: BatchInput[K]) => setF((p) => ({ ...p, [k]: v }));
  const setS = (i: number, patch: Partial<BatchSession>) =>
    setF((p) => ({ ...p, sessions: p.sessions.map((s, n) => (n === i ? { ...s, ...patch } : s)) }));

  async function submit(state: PubState) {
    if (loadingSessions) { setError('Still loading this batch — give it a moment.'); return; }
    if (!f.name.trim()) { setError('A batch name is required.'); return; }
    if (state === 'published' && !f.starts_on) {
      setError('A published batch needs a start date — the site puts it on the page and members plan around it.');
      return;
    }
    if (f.seats_sold > f.seats_total && f.seats_total > 0) {
      setError('More seats sold than exist. The page would show a negative number of seats left.');
      return;
    }
    setError('');
    setStatus(state === 'published' ? 'Publishing…' : 'Saving…');
    try {
      const body = { ...f, state, sessions: f.sessions.filter((s) => s.topic.trim() || s.starts_at.trim()) };
      if (batch) await Api.update(batch.id, body);
      else await Api.create(body);
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={batch ? `Edit batch · ${batch.name}` : 'New batch'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>
            {batch?.state === 'published' ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <Row cols="2fr 1fr">
        <Field label="Name"><input className="inp" value={f.name} autoFocus
          onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Platform"><input className="inp" value={f.platform} placeholder="Zoom"
          onChange={(e) => set('platform', e.target.value)} /></Field>
      </Row>

      <Field label="Subtitle"><input className="inp" value={f.subtitle}
        onChange={(e) => set('subtitle', e.target.value)} /></Field>

      <Row>
        <Field label="Starts on"><input className="inp" type="date" value={f.starts_on}
          onChange={(e) => set('starts_on', e.target.value)} /></Field>
        <Field label="Time"><input className="inp" value={f.start_time} placeholder="8:00 PM IST"
          onChange={(e) => set('start_time', e.target.value)} /></Field>
        <Field label="Fee (₹)"><input className="inp" type="number" min={0} value={f.fee || ''}
          onChange={(e) => set('fee', Number(e.target.value))} /></Field>
      </Row>

      <Row>
        <Field label="Seats total"><input className="inp" type="number" min={0} value={f.seats_total || ''}
          onChange={(e) => set('seats_total', Number(e.target.value))} /></Field>
        <Field label="Seats sold" hint="What the page counts down from.">
          <input className="inp" type="number" min={0} value={f.seats_sold || ''}
            onChange={(e) => set('seats_sold', Number(e.target.value))} /></Field>
        <Field label="Retakes"><input className="inp" value={f.retakes}
          onChange={(e) => set('retakes', e.target.value)} /></Field>
      </Row>

      <ImagePick label="Batch thumbnail" path={cover} alt={f.name}
        hint="Shown on the live classes page, on the batch's own page and in the member area. The whole picture is shown — nothing is cropped — and a 16:9 landscape fills the space exactly."
        onPick={(id, p) => { set('cover_media_id', id); setCover(p); }} />

      <div className="fld">
        <label className="lbl">Timetable{loadingSessions ? ' — loading…' : ''}</label>
        {f.sessions.map((s, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 200px 1fr 34px', gap: 8, marginBottom: 8 }}>
            <input className="inp" placeholder="Topic" value={s.topic}
              onChange={(e) => setS(i, { topic: e.target.value })} />
            <input className="inp" type="datetime-local" value={(s.starts_at || '').replace(' ', 'T').slice(0, 16)}
              onChange={(e) => setS(i, { starts_at: e.target.value.replace('T', ' ') + ':00' })} />
            <input className="inp" placeholder="Meeting link" value={s.meeting_link}
              onChange={(e) => setS(i, { meeting_link: e.target.value })} />
            <button className="b bo" type="button" style={{ padding: 0 }} aria-label="Remove"
              onClick={() => setF((p) => ({
                ...p, sessions: p.sessions.length === 1 ? [blankSession()] : p.sessions.filter((_, n) => n !== i),
              }))}>×</button>
          </div>
        ))}
        <button className="b bo" type="button" style={{ padding: '7px 12px', fontSize: 12 }}
          onClick={() => setF((p) => ({ ...p, sessions: [...p.sessions, blankSession()] }))}>Add a session</button>
      </div>
    </Modal>
  );
}
